import { defineComponent as _defineComponent } from 'vue'
import { getCurrencyRate } from "@/api/catalogue";
import {
    createChargeRequestV2,
    editChargeRequestV2,
    getAffiliateInvoices,
    getChargeRequestV2,
    getContractors,
    getCurrencies,
    getDds,
    getEmployeeAffiliateMethods,
    getOrganizations,
    getPaymentMethodsV2,
    getProjects,
} from "@/api/payout";
import {
    CommonType,
    ICreateChargeRequestForm,
    ICreateChargeRequestPaymentCard,
    IEmployeeAffiliateMethod,
    IMyLeadgidInvoice,
} from "@/api/types/payout";
import { IAffiliateItem } from "@/api/types/user";
import { getAffiliateSearch, getEmployeesAffiliates } from '@/api/user';
import SvgArrowRight from "@/assets/icons/arrow-right.svg";
import SvgCheck from "@/assets/icons/check.svg";
import SvgMasterInvoice from "@/assets/icons/master-invoice.svg";
import SvgPlus from "@/assets/icons/plus.svg";
import SvgReload from "@/assets/icons/reload.svg";
import SvgTrash from "@/assets/icons/trash-dark.svg";
import BaseAutocomplete from "@/components-UI/BaseAutocomplete.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseInput from "@/components-UI/BaseInput.vue";
import BaseInputNumber from "@/components-UI/BaseInputNumber.vue";
import BaseQuestionMarkTooltip from "@/components-UI/BaseQuestionMarkTooltip.vue";
import BaseSelect from "@/components-UI/BaseSelect.vue";
import BaseTextarea from "@/components-UI/BaseTextarea.vue";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import BaseUpload from "@/components-UI/BaseUpload.vue";
import TrashButton from "@/components-UI/buttons/TrashButton.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import Status from "@/components/base/Status.vue";
import PageLayout from "@/components_v3/layout/PageLayout.vue";
import router from "@/router";
import { IStatus } from "@/types";
import { ITableHeader, showNotification, showServerError } from "@/utils";
import { CellsType, prepareItems, RawConfig } from "@/utils/CellsFactory";
import { getCurrentDate } from "@/utils/formatDates";
import {
    getCurrencyIcon,
    translateChargeRequestStatus,
    translatePartnerStatus,
} from "@/utils/translate";
import { computed, onMounted, ref } from "vue";

// VARIABLES _________________

export default /*#__PURE__*/_defineComponent({
  __name: 'CreateChargeRequest',
  setup(__props) {

const projectsRequiredFile = ['Лидгид Европа', 'Лидгид Казахстан'];
const projectsOptionalFile = ['Лидгид Россия (валюта)', 'Лидгид рубли', 'СМС Рифмир', 'Лидгид Казахстан (валюта)'];
const allFileProjects = [...projectsRequiredFile, ...projectsOptionalFile];

const pageLoading = ref(false);

const formatter = new Intl.NumberFormat('ru-RU', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const currentChargeRequestStatus = ref('');
const form = ref<ICreateChargeRequestForm>({
    dds: {},
    affiliatesIds: [],
    paymentsCards: [
        {
            organization: {},
            project: {},
            paymentMethod: {},
            details: {},
            anotherOptionText: '',
            anotherOptionFile: [],
            counterparty: {},
            accounts: [],
            accountsTableItems: [],
            currency: {},
            payout: null,
            commission: null,
            totalSum: '',
            exchangeRate: [],
            comment: '',
            invoice_number: '',
        },
    ],
});

const affiliateCardTableItems = ref<IAffiliateItem[]>([]);
const affiliateCardTableHeaders = ref<ITableHeader[]>([
    { text: 'ID мастера', value: 'master_id' },
    { text: 'ID партнёра', value: 'id' },
    { text: 'Имя партнёра', value: 'affiliate_name' },
    { text: 'Почта', value: 'email' },
    { text: 'Аккаунт-менеджер', value: 'manager_name' },
    { text: 'Договор', value: 'contract_type' },
    { text: 'Статус', value: 'status' },
    { text: '', value: 'actions', align: 'center', width: '48px' },
]);
const affiliateCardTableItemsConfig = ref<RawConfig<IAffiliateItem>>({
    [CellsType.Route]: {
        master_id: {
            name: 'affiliate',
            valueKey: 'master_id',
            isChip: true,
            text: 'master_id',
            isMasterChip: true,
        },
        id: {
            name: 'affiliate',
            valueKey: 'id',
            isChip: true,
            text: 'id',
        },
    },
    [CellsType.Text]: {
        contract_type: {
            template: (i: IAffiliateItem) => !!i?.agreement_with_legal_entity ? 'Да' : 'Нет',
        },
        affiliate_name: {
            template: (i: IAffiliateItem) => `${i.first_name} ${i.last_name}`,
        },
        manager_name: {
            template: (i: IAffiliateItem) => `${i.manager?.first_name} ${i.manager?.last_name}`,
        },
    },
    [CellsType.Status]: {
        status: {
            translate: (str: string) => translatePartnerStatus(str),
        },
    },
    [CellsType.Uniq]: {
        actions: {},
    },
});

const paymentCardTableHeaders = ref<ITableHeader[]>([
    { text: 'ID счёта', value: 'id', width: '118px', align: 'center' },
    { text: 'ID партнёра', value: 'affiliate_id', width: 'auto' },
    { text: 'Валюта', value: 'currency', width: 'auto' },
    { text: 'Сумма счёта', value: 'total_amount', width: 'auto' },
    { text: 'Остаток', value: 'rest_amount', width: 'auto' },
    { text: 'К выплате', value: 'field', width: '192px', align: 'end' },
    { text: '', value: 'actions', width: '48px', align: 'center' },
]);
const paymentCardTableItemsConfig = ref<RawConfig<IMyLeadgidInvoice>>({
    [CellsType.Route]: {
        id: {
            name: 'affiliate-invoice',
            valueKey: 'id',
            isChip: true,
            text: 'id',
            icon: (i: IMyLeadgidInvoice) => i.is_master ? SvgMasterInvoice : undefined,
            tooltip: (i: IMyLeadgidInvoice) => i.is_master ? 'Мастер-счёт' : '',
            iconIndent: 6,
            isTextCenter: true,
        },
        affiliate_id: {
            name: 'affiliate',
            valueKey: 'affiliate_id',
            isChip: true,
            text: 'affiliate_id',
        },
    },
    [CellsType.Number]: {
        total_amount: {},
        rest_amount: {},
    },
    [CellsType.Uniq]: {
        actions: {},
        field: {},
    },
});

const organizationItems = ref<CommonType[]>([]);
const projectItems = ref<CommonType[]>([]);
const paymentMethodsItems = ref<CommonType[]>([]);
const detailsItems = ref<Array<CommonType & { commission: null | number }>>([]);
const accountsItems = ref<IMyLeadgidInvoice[]>([]);
const affiliatesItems = ref<IAffiliateItem[]>([]);
const ddsItems = ref<CommonType[]>([]);
const currenciesItems = ref<CommonType[]>([]);

const projectsIncludeMap = [
    'Лидгид Казахстан (карта)',
    'Лидгид рубли (карта)',
    'Лидгид Европа (карта)',
    'Производство WV (карта)',
];

const isFooterBg = ref(false);

// COMPUTED _________________

const getAccounts = (card: ICreateChargeRequestPaymentCard) => computed(() => {
    const tableAccountsIds = card.accountsTableItems.map(el => el.id);
    const noDuplicatesList = accountsItems.value.filter(el => !tableAccountsIds.includes(el.id));

    if (!card.accounts.length && !card.accountsTableItems.length || card.paymentMethod.guid === 'contract') {
        return noDuplicatesList;
    }

    if (!!card.accounts.length && !card.accountsTableItems.length) {
        const selectedCurrencies = noDuplicatesList.filter(el => card.accounts.includes(el.id)).map(el => el.currency);
        const hasRubOrKzt = selectedCurrencies.some(el => el === 'RUB' || el === 'KZT');

        return noDuplicatesList.filter(el => {
            if (hasRubOrKzt) {
                return el.currency === 'RUB' || el.currency === 'KZT';
            } else {
                return selectedCurrencies.includes(el.currency);
            }
        });
    }

    return noDuplicatesList.filter(el => {
        const selectedCurrencies = card.accountsTableItems.map(item => item.currency);
        const hasRubOrKzt = selectedCurrencies.some(currency => currency === 'RUB' || currency === 'KZT');

        if (hasRubOrKzt) {
            return el.currency === 'RUB' || el.currency === 'KZT';
        } else {
            return selectedCurrencies.includes(el.currency);
        }
    });
});

const affiliateCardFormatTableItems = computed(() => {
    const columns = affiliateCardTableHeaders.value.map(h => h.value);
    return prepareItems(affiliateCardTableItems.value, affiliateCardTableItemsConfig.value, columns);
});

const paymentCardFormatTableItems = (card: ICreateChargeRequestPaymentCard) => computed(() => {
    const columns = paymentCardTableHeaders.value.map(h => h.value);
    return prepareItems(card.accountsTableItems, paymentCardTableItemsConfig.value, columns);
});

const getAccountSelectLabel = (i: IMyLeadgidInvoice) => computed(() => {
    const totalAmount = Number(i.total_amount).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${i.affiliate_id} - счёт ${i.id}: ${totalAmount} ${i.currency}`;
});

const getCurrentRouteId = computed<string>(() => router.currentRoute.params.id);

const isDisabledSaveButton = computed<boolean>(() => {
    return form.value.paymentsCards.some((v: ICreateChargeRequestPaymentCard) => {
        return !affiliateCardTableItems.value.length ||
            !v.organization?.guid ||
            !v.paymentMethod?.guid ||
            !v.project?.guid ||
            !v.currency.guid ||
            v.exchangeRate.some(el => !el.rate) ||
            !v.accountsTableItems.length ||
            !v.payout ||
            v.commission === null ||
            (v.details?.id === 'another_option' ? !v.anotherOptionText : !v.details?.id) &&
            (
                v.paymentMethod.name === 'Revolut' ? !v.anotherOptionFile.length :
                    v.paymentMethod?.guid === 'contract' && projectsRequiredFile.includes(v.project?.name) ?
                        !v.anotherOptionFile.length || !v.counterparty?.guid :
                        v.paymentMethod?.guid === 'contract' && !projectsRequiredFile.includes(v.project?.name) ?
                            !v.counterparty?.guid : true
            );
    });
});

const getPageTitle = computed<string>(() => {
    return !getCurrentRouteId.value ? `Создание заявки на расход` : `Заявка на расход №${getCurrentRouteId.value}`;
});

const getStatus = computed<IStatus>(() => {
    return translateChargeRequestStatus(currentChargeRequestStatus.value);
});

// FUNCTIONS _________________

const getPaymentMethods = (projectName: string): CommonType[] => {
    if (projectsIncludeMap.includes(projectName)) {
        return paymentMethodsItems.value.filter(el => el.name === 'Карта');
    }

    return paymentMethodsItems.value.filter(el => el.name !== 'Карта');
};

const handleClickBackButton = (): void => {
    const route: { name: string, params?: { id: string } } = { name: '' };

    if (getCurrentRouteId.value) {
        route.name = 'charge-request';
        route.params = { id: getCurrentRouteId.value };
    } else {
        route.name = 'charge-requests';
    }

    router.push(route);
};

const handleUpdateProject = async (card: ICreateChargeRequestPaymentCard, item: CommonType): Promise<void> => {
    try {
        card.project = item;
        card.organization = {} as CommonType;
        card.anotherOptionFile = [];

        organizationItems.value = await getOrganizations(item.name);
        card.paymentMethod = {} as CommonType;
    } catch (e) {
        showServerError(e, 'Организации не загружены');
    }
};

const handleSelectAffiliates = ({ item }: { item: IAffiliateItem }): void => {
    affiliatesItems.value.push(item);
};

const handleUpdateAffiliates = (event: number[]): void => {
    form.value.affiliatesIds = event;

    affiliatesItems.value.forEach((i: IAffiliateItem, idx: number) => {
        if (!form.value.affiliatesIds.includes(i.id)) {
            affiliatesItems.value.splice(idx, 1);
        }
    });
};

const handleUpdatePaymentMethod = async (
    item: CommonType,
    card: ICreateChargeRequestPaymentCard,
): Promise<void> => {
    card.details = {} as CommonType;
    card.counterparty = {} as CommonType;
    card.paymentMethod = item;
    card.anotherOptionFile = [];
    card.anotherOptionText = '';

    if (
        !item.name ||
        item.name === 'По договору' ||
        item.name === 'Revolut'
    ) return;

    try {
        const data = await getEmployeeAffiliateMethods({
            offset: 0,
            limit: 100,
            statuses: ['approved'],
            partner_ids: affiliateCardTableItems.value.map((i: IAffiliateItem) => i.id),
            method_name: item.name,
        });

        detailsItems.value = data.data.map((i: IEmployeeAffiliateMethod) => {
            const partnerDataKey = Object.keys(i.partner_data)[0];

            return {
                id: i.id || partnerDataKey,
                name: i.partner_data[partnerDataKey],
                commission: i.commission,
            };
        });
        detailsItems.value = [
            ...detailsItems.value,
            ...[{ name: 'Свой вариант', id: 'another_option' }],
        ];
    } catch (e) {
        showServerError(e, 'Реквизиты не загружены');
    }
};

const handleUpdatePaymentCurrency = async (
    card: ICreateChargeRequestPaymentCard,
    item?: CommonType,
): Promise<void> => {
    try {
        card.currency = item!;
        card.exchangeRate = [];

        if (card.details.commission) {
            card.commission = card.details.commission * 100;
        }

        const uniqueCurrencies = Array.from(
            new Map(card.accountsTableItems.map(i => [i.currency, i])).values(),
        );

        for (const i of uniqueCurrencies) {
            if (item?.name === i.currency) continue;

            const { original_rate, operation }: {
                original_rate: number,
                operation: 'division' | 'multiplication'
            } = await getCurrencyRate({
                source: 'excel',
                currency_from: i.currency,
                currency_to: card.currency.name,
                date: getCurrentDate('yyyy-MM-dd'),
            }).catch(e => {
                if (e.status === 404) {
                    return { rate: 0, operation: 'multiplication' };
                }
            });

            card.exchangeRate.push({
                from: i.currency,
                to: card.currency.name || '',
                rate: original_rate,
                operation,
            });
        }

        recountSum(card);
    } catch (e) {
        showServerError(e, 'Список валют не загружен');
    }
};

const handleClearPaymentCurrency = (card: ICreateChargeRequestPaymentCard): void => {
    card.currency = {} as CommonType;
    card.payout = null;
    card.totalSum = '0';
    card.exchangeRate = [];
};

const handleClickSaveButton = async (): Promise<void> => {
    if (!getCurrentRouteId.value) {
        await saveChargeRequest();
    } else {
        await editChargeRequest();
    }
};

const handleUpdateRequisites = (card: ICreateChargeRequestPaymentCard, item: CommonType): void => {
    card.details = item;
    card.anotherOptionText = '';
};

const addAffiliateInTable = async (): Promise<void> => {
    try {
        for (const i of affiliatesItems.value) {
            const isExisting = affiliateCardTableItems.value.some(item => item.id === i.id);

            if (!isExisting) {
                affiliateCardTableItems.value.push(i);
            }
        }

        const data = await Promise.all([
            getAffiliateInvoices({
                offset: 0,
                limit: 500,
                affiliate_id: affiliateCardTableItems.value.map(el => el.id),
                paid_status: ['partial_paid', 'not_paid'],
            }),
            getPaymentMethodsV2(true),
        ]);

        accountsItems.value = data[0].data;
        paymentMethodsItems.value = data[1];
        affiliatesItems.value = [];
        form.value.affiliatesIds = [];
    } catch (e) {
        showServerError(e, 'Счета не получены');
    }
};

const deleteAffiliateFromTable = async (item: IAffiliateItem): Promise<void> => {
    const deletedAffiliateId = item.id;

    affiliateCardTableItems.value = affiliateCardTableItems.value.filter(
        (i: IAffiliateItem) => i.user_id !== item.user_id,
    );

    form.value.paymentsCards.forEach((card: ICreateChargeRequestPaymentCard) => {
        card.accountsTableItems = card.accountsTableItems.filter(
            (invoice: IMyLeadgidInvoice) => invoice.affiliate_id !== deletedAffiliateId,
        );
        const arrCurrencies = new Set(card.accountsTableItems.map(inv => inv.currency));
        card.exchangeRate = card.exchangeRate.filter(rate => arrCurrencies.has(rate.from));
        recountSum(card);
    });

    if (!affiliateCardTableItems.value.length) {
        accountsItems.value = [];
    } else {
        try {
            const data = await getAffiliateInvoices({
                offset: 0,
                limit: 500,
                affiliate_id: affiliateCardTableItems.value.map(el => el.id),
                paid_status: ['partial_paid', 'not_paid'],
            });
            accountsItems.value = data.data;
        } catch (e) {
            showServerError(e, 'Счета не получены');
        }
    }
};

const addAccountInTable = (card: ICreateChargeRequestPaymentCard): void => {
    card.accountsTableItems = JSON.parse(JSON.stringify([
        ...card.accountsTableItems,
        ...accountsItems.value.filter(el => card.accounts.includes(el.id)),
    ]));
    card.accounts = [];

    if (card.currency?.guid) {
        handleUpdatePaymentCurrency(card, card.currency);
    }
};

const deleteAccountInTable = (card: ICreateChargeRequestPaymentCard, i: IMyLeadgidInvoice): void => {
    card.accountsTableItems.forEach((el, idx) => {
        if (i.id === el.id) {
            card.accountsTableItems.splice(idx, 1);
        }
    });

    const arr1Currencies = new Set(card.accountsTableItems.map(item => item.currency));
    card.exchangeRate = card.exchangeRate.filter(item => arr1Currencies.has(item.from));

    recountSum(card);
};

const deleteAllAccountsInTable = (card: ICreateChargeRequestPaymentCard): void => {
    card.accountsTableItems = [];
    card.exchangeRate = [];
};

const recountSum = (card: ICreateChargeRequestPaymentCard, isPayout: boolean = false): void => {
    if (isPayout) {
        card.totalSum = String(card.payout - card.payout * (card.commission / 100));
        return;
    }

    const rateMap = new Map<string, { rate: number; operation: 'division' | 'multiply' }>(
        card.exchangeRate.map(rate => [rate.from, { rate: +rate.rate, operation: rate.operation }]),
    );

    const payout = card.accountsTableItems.reduce((sum, item) => {
        const restAmount = +(
            item?.rest_amount_editable ??
            item?.rest_amount ??
            0
        );
        const rateInfo = rateMap.get(item.currency);

        if (!rateInfo || rateInfo.rate <= 0) {
            return sum + restAmount;
        }

        const { rate, operation } = rateInfo;
        return sum + (operation === 'division' ? restAmount / rate : restAmount * rate);
    }, 0);

    card.payout = payout;
    card.totalSum = String(payout - payout * (card.commission / 100));
};


const addPaymentCard = (): void => {
    form.value.paymentsCards.push({
        organization: {},
        project: {},
        paymentMethod: {},
        details: {},
        anotherOptionText: '',
        anotherOptionFile: [],
        counterparty: {},
        accounts: [],
        accountsTableItems: [],
        currency: {},
        payout: null,
        commission: null,
        exchangeRate: [],
        comment: '',
    });
    isFooterBg.value = true;
};

const deletePaymentCard = (index: number): void => {
    form.value.paymentsCards.splice(index, 1);
};

const fetchData = async (): Promise<void> => {
    try {
        pageLoading.value = true;

        const [
            dds,
            projects,
            currencies,
        ] = await Promise.all([
            getDds(),
            getProjects(),
            getCurrencies(),
        ]);
        ddsItems.value = dds.rest;
        form.value.dds = dds.default_dds;
        projectItems.value = projects;
        currenciesItems.value = currencies.sort((a, b) => a === 'AED' ? -1 : b === 'AED' ? 1 : 0);
    } catch (e) {
        showServerError(e, 'Ну удалось получить данные');
    } finally {
        pageLoading.value = false;
    }
};

const saveChargeRequest = async (): Promise<void> => {
    try {
        const formData = new FormData();
        form.value.paymentsCards.forEach((el, idx) => {
            formData.append(`items[${idx}][dds]`, form.value.dds.guid);
            formData.append(`items[${idx}][org]`, el.organization.guid);
            formData.append(`items[${idx}][project]`, el.project.guid);
            formData.append(`items[${idx}][requisites]`, el.anotherOptionText || el.details.name || '');
            formData.append(`items[${idx}][contractor]`, el.counterparty.guid || el.paymentMethod.guid);
            formData.append(`items[${idx}][currency]`, el.currency.name!);
            formData.append(`items[${idx}][amount]`, el.payout.toFixed(2));
            formData.append(`items[${idx}][comment]`, el.comment);
            formData.append(`items[${idx}][contract_type]`, el.paymentMethod.guid === 'contract' ? el.paymentMethod.guid : 'default');
            formData.append(`items[${idx}][commission]`, (el.commission / 100).toFixed(4));
            if (el.invoice_number) {
                formData.append(`items[${idx}][invoice_number]`, el.paymentMethod.guid === 'contract' && el.project.name === 'Лидгид Европа' ? el.invoice_number : '');
            }

            affiliateCardTableItems.value.forEach(e => {
                formData.append(`items[${idx}][affiliates_ids][]`, String(e.id));
            });

            el.exchangeRate.forEach((e, i) => {
                if (!!Number(e.rate)) {
                    formData.append(`items[${idx}][currency_rates][${i}][to]`, e.to);
                    formData.append(`items[${idx}][currency_rates][${i}][from]`, e.from);
                    formData.append(`items[${idx}][currency_rates][${i}][rate]`, e.rate);
                }
            });

            el.accountsTableItems.forEach(e => {
                formData.append(`items[${idx}][invoices_ids][]`, String(e.id));
                formData.append(`items[${idx}][invoices_payments][${e.id}]`, e?.rest_amount_editable ? String(e.rest_amount_editable) : String(e.rest_amount));
            });

            if (!!el.anotherOptionFile.length) {
                formData.append(`files[${idx}]`, el.anotherOptionFile[0]);
            }
        });

        await createChargeRequestV2(formData);
        router.push({ name: 'charge-requests' });
        showNotification('Заявка успешно создана');
    } catch (e) {
        showServerError(e, 'Не удалось сохранить заявку на выплату');
    }
};

const defaultData: any = {};

const editChargeRequest = async (): Promise<void> => {
    try {
        const card = form.value.paymentsCards[0];
        const formData = new FormData();
        affiliateCardTableItems.value.forEach(el => {
            formData.append('affiliates_ids[]', el.id);
        });
        card.accountsTableItems.forEach(el => {
            formData.append('invoices_ids[]', el.id);
        });
        card.accountsTableItems.forEach(el => {
            formData.append(`invoices_payments[${el.id}]`, el?.rest_amount_editable ? String(el.rest_amount_editable) : String(el.rest_amount));
        });
        card.exchangeRate.forEach((el, idx) => {
            if (!!Number(el.rate)) {
                formData.append(`currency_rates[${idx}][to]`, el.to);
                formData.append(`currency_rates[${idx}][from]`, el.from);
                formData.append(`currency_rates[${idx}][rate]`, el.rate);
            }
        });

        if (form.value.dds.guid !== defaultData.dds) {
            formData.append('dds', form.value.dds.guid);
        }

        if (card.organization.guid !== defaultData.organization) {
            formData.append('organization', card.organization.guid);
        }

        if (card.paymentMethod.guid !== defaultData.paymentMethod) {
            formData.append('contractor', card.counterparty.guid || card.paymentMethod.guid);
        }

        if (card.paymentMethod.guid === 'contract' && card.paymentMethod.guid !== defaultData.contractType) {
            formData.append('contract_type', card.paymentMethod.guid);
        }

        if (card.paymentMethod.guid !== 'contract' && card.paymentMethod.guid !== defaultData.contractType) {
            formData.append('contract_type', 'default');
        }

        if (card.details.id === 'another_option' && card.anotherOptionText !== defaultData.details) {
            formData.append('requisites', card.anotherOptionText);
        }

        if (!!card.details.id && card.details.id !== 'another_option' && card.details.name !== defaultData.details) {
            formData.append('requisites', card.details.name);
        }

        if (!!card.anotherOptionFile.length && card.anotherOptionFile[0].name !== defaultData.anotherOptionFile) {
            formData.append('file', card.anotherOptionFile[0]);
        }

        if (card.currency.guid !== defaultData.currency) {
            formData.append('currency', card.currency.name);
        }

        if (card.payout !== defaultData.payout) {
            formData.append('amount', card.payout.toFixed(2));
        }

        if (card.commission !== defaultData.commission) {
            formData.append('commission', (card.commission / 100).toFixed(4));
        }

        if (card.comment !== defaultData.comment) {
            formData.append('comment', card.comment);
        }

        if (card.invoice_number !== defaultData.invoice_number) {
            formData.append('invoice_number', card.invoice_number);
        }

        await editChargeRequestV2(getCurrentRouteId.value, formData);
        router.push({
            name: 'charge-request',
            params: {
                id: getCurrentRouteId.value,
            },
        });
        showNotification('Заявка успешно изменена');
    } catch (e) {
        showServerError(e, 'Не удалось редактировать заявку');
    }
};

const setChargeRequestForEdit = async (): Promise<void> => {
    try {
        if (getCurrentRouteId.value === undefined || isNaN(Number(getCurrentRouteId.value))) return;

        pageLoading.value = true;

        const data = await getChargeRequestV2(getCurrentRouteId.value);
        const affiliates = await getEmployeesAffiliates({
            ids: data.affiliates,
            fields: [
                'master_id',
                'id',
                'first_name',
                'last_name',
                'email',
                'manager',
                'agreement_with_legal_entity',
                'status',
            ],
        });
        affiliateCardTableItems.value = affiliates.data;
        await addAffiliateInTable();

        form.value.dds = data.dds;

        currentChargeRequestStatus.value = data.status;

        await handleUpdateProject(form.value.paymentsCards[0], data.project);
        form.value.paymentsCards[0].organization = {
            ...data.legal,
            ...{ name: data.legal.full_name },
        };

        if (data.contract_type === 'contract') {
            form.value.paymentsCards[0].paymentMethod = {
                name: 'По договору',
                guid: 'contract',
            };
            form.value.paymentsCards[0].counterparty = data.contractor;

            if (projectsRequiredFile.includes(data.project.name)) {
                form.value.paymentsCards[0].anotherOptionFile = data.attachment ? [data.attachment] : [];
            }
        } else {
            if (data.contractor.full_name === 'Revolut') {
                form.value.paymentsCards[0].paymentMethod = {
                    ...data.contractor,
                    ...{ name: data.contractor.full_name },
                };
                form.value.paymentsCards[0].anotherOptionFile = data.attachment ? [data.attachment] : [];
            } else {
                form.value.paymentsCards[0].paymentMethod = {
                    ...data.contractor,
                    ...{ name: data.contractor.full_name },
                };
                detailsItems.value = [{
                    name: 'Свой вариант',
                    id: 'another_option',
                }];
                form.value.paymentsCards[0].details = {
                    name: 'Свой вариант',
                    id: 'another_option',
                };
                form.value.paymentsCards[0].anotherOptionText = data.requisites;
            }
        }

        form.value.paymentsCards[0].accountsTableItems = data.invoices.map(i => ({
            ...i,
            ...{
                total_amount: i.payout,
                rest_amount: i.balance,
                rest_amount_editable: i.paid,
            },
        }));

        form.value.paymentsCards[0].currency = data.currency;
        form.value.paymentsCards[0].exchangeRate = data.currency_rates;
        form.value.paymentsCards[0].payout = data.amount;
        form.value.paymentsCards[0].commission = data.commission * 100;
        form.value.paymentsCards[0].totalSum = data.total_amount;
        form.value.paymentsCards[0].comment = data.comment;
        form.value.paymentsCards[0].invoice_number = data.invoice_number;

        defaultData.dds = data.dds.guid;
        defaultData.affiliatesIds = data.affiliates;
        defaultData.organization = data.legal.guid;
        defaultData.paymentMethod = form.value.paymentsCards[0].paymentMethod.guid;

        if (form.value.paymentsCards[0].counterparty.guid) {
            defaultData.counterparty = form.value.paymentsCards[0].counterparty.guid;
        }

        if (!!form.value.paymentsCards[0].anotherOptionFile.length) {
            defaultData.anotherOptionFile = form.value.paymentsCards[0].anotherOptionFile[0].name;
        }

        if (form.value.paymentsCards[0].details.id) {
            defaultData.details = data.requisites;
        }

        defaultData.accountsTableItems = data.invoices.map(i => ({
            id: i.id,
            amount: i.total_payout,
        }));
        defaultData.currency = data.currency.guid;
        defaultData.exchangeRate = data.currency_rates;
        defaultData.payout = data.amount;
        defaultData.commission = data.commission * 100;
        defaultData.comment = data.comment;
        defaultData.contractType = data.contract_type;
        defaultData.invoice_number = data.invoice_number;
    } catch (e) {
        showServerError(e, 'Заявка на расход не получена');
    } finally {
        pageLoading.value = false;
    }
};

// VUE HOOKS _________________

router.beforeEach((to, _, next) => {
    if (to.fullPath === '/affiliates/create-charge-request') {
        window.location.href = to.fullPath;
    }

    next();
});

window.onload = function () {
    const perf = window.performance;

    if (perf && perf.navigation.type === 1 && getCurrentRouteId.value) {
        router.push({
            name: 'charge-request',
            params: {
                id: getCurrentRouteId.value,
            },
        });
    }
};

onMounted(async (): Promise<void> => {
    await fetchData();
    await setChargeRequestForEdit();

    const HTML = document.querySelector('.page')?.children[0];
    const thisPage = document.querySelector('.page-layout__content');

    if (HTML) {
        HTML.style.overflow = 'unset !important';
    }

    if (thisPage && thisPage.clientHeight < 606) {
        isFooterBg.value = true;
    }

    thisPage?.addEventListener('scroll', (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        isFooterBg.value = !(scrollTop + clientHeight >= scrollHeight - 2);
    });
});

return { __sfc: true,projectsRequiredFile, projectsOptionalFile, allFileProjects, pageLoading, formatter, currentChargeRequestStatus, form, affiliateCardTableItems, affiliateCardTableHeaders, affiliateCardTableItemsConfig, paymentCardTableHeaders, paymentCardTableItemsConfig, organizationItems, projectItems, paymentMethodsItems, detailsItems, accountsItems, affiliatesItems, ddsItems, currenciesItems, projectsIncludeMap, isFooterBg, getAccounts, affiliateCardFormatTableItems, paymentCardFormatTableItems, getAccountSelectLabel, getCurrentRouteId, isDisabledSaveButton, getPageTitle, getStatus, getPaymentMethods, handleClickBackButton, handleUpdateProject, handleSelectAffiliates, handleUpdateAffiliates, handleUpdatePaymentMethod, handleUpdatePaymentCurrency, handleClearPaymentCurrency, handleClickSaveButton, handleUpdateRequisites, addAffiliateInTable, deleteAffiliateFromTable, addAccountInTable, deleteAccountInTable, deleteAllAccountsInTable, recountSum, addPaymentCard, deletePaymentCard, fetchData, saveChargeRequest, defaultData, editChargeRequest, setChargeRequestForEdit, getContractors, getAffiliateSearch, SvgArrowRight, SvgCheck, SvgPlus, SvgReload, SvgTrash, BaseAutocomplete, BaseButton, BaseEditableCard, BaseInput, BaseInputNumber, BaseQuestionMarkTooltip, BaseSelect, BaseTextarea, BaseTooltip, BaseUpload, TrashButton, BaseList, BaseListItem, BaseTable, Status, PageLayout, router, getCurrencyIcon }
}

})